// ABOUTME: The filter sheet of the feed: a place to measure from, the job's shape, occupation and company, whether to include
// ABOUTME: what I applied to, and saving where I stand or the search itself. It hands back one criteria object.
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { AppButton } from 'components/AppButton';
import { BottomSheet } from 'components/BottomSheet';
import { Text } from 'components/Text';
import { PATHS } from 'config/paths';
import { getTrans, getTransWithParams, TranslationKey } from 'modules/i18n';
import { MAX_LABEL_LENGTH, MAX_SAVED_POINTS } from '../constants';
import {
  ApplicationStatusFilter,
  FilterOption,
  FilterOptions,
  JobListingSearchCriteria,
  JobListingSort,
  SavedPoint,
  SavedSearch,
  SearchPreferences,
  WorkingTimeMode,
  WorkplaceMode
} from '../types';
import {
  DEFAULT_CRITERIA,
  LocateError,
  LocateFailure,
  locateMe
} from '../utils';

interface Props {
  open: boolean;
  onClose: () => void;
  criteria: JobListingSearchCriteria;
  savedPoints: SavedPoint[];
  /**
   * Whether the saved points and searches were read. Until then the applied point cannot be matched to a saved one,
   * so it is kept as it is, and nothing is saved: a save replaces the stored document whole.
   */
  preferencesLoaded: boolean;
  /** What the occupation and company filters offer; an empty list is a filter not offered. */
  filterOptions: FilterOptions;
  /** Whether the options arrived; until then a refId in the criteria is kept even though no option names it. */
  filterOptionsLoaded: boolean;
  onApply: (criteria: JobListingSearchCriteria) => void;
  /** Adds a point and answers the document as stored, with the refId the server minted for it. */
  onSavePoint: (point: SavedPoint) => Promise<SearchPreferences>;
  /** Adds or replaces a saved search by its label. */
  onSaveSearch: (search: SavedSearch) => Promise<SearchPreferences>;
  /** PJL (PRO_LISTING_FEED): offer the week fit, workplace and working-time filters; off, the sheet is today's. */
  pjl?: boolean;
}

const WORKPLACE_MODES: WorkplaceMode[] = ['ON_SITE', 'HYBRID', 'REMOTE'];
const WORKING_TIME_MODES: WorkingTimeMode[] = ['BY_ARRANGEMENT', 'FIXED'];

/** Several of a few modes, as toggle chips; none chosen narrows nothing. */
const ModeChoice = <T extends string>({
  title,
  testId,
  options,
  labelPrefix,
  value,
  onChange
}: {
  title: string;
  testId: string;
  options: T[];
  labelPrefix: string;
  value: T[];
  onChange: (value: T[]) => void;
}) => (
  <Box data-testid={testId}>
    <Text variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
      {title}
    </Text>
    <ToggleButtonGroup
      value={value}
      onChange={(_, next: T[]) => onChange(next)}
      aria-label={title}
      size="small"
    >
      {options.map(option => (
        <ToggleButton key={option} value={option}>
          {getTrans(`${labelPrefix}.${option}` as TranslationKey)}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  </Box>
);

const RADII_KM = [5, 10, 25, 50];
/** Five-hour steps in minutes, the granularity a working student thinks in. */
const HOURS_MINUTES = [300, 600, 900, 1200, 1500, 1800, 2400];
const DAYS = [1, 2, 3, 4, 5, 6, 7];

const NONE = '';
/** The applied point while the saved points are still unknown: kept as applied, matched once they arrive. */
const UNRESOLVED = '__applied__';

/** The sheet's own draft of the criteria, with the point as the saved point's refId so a select can hold it. */
interface Draft {
  pointRefId: string;
  radiusKm: string;
  hourlyRateMin: string;
  remoteOnly: boolean;
  startDateFrom: string;
  workingDays: number[];
  weeklyHoursMinMinutes: string;
  weeklyHoursMaxMinutes: string;
  query: string;
  occupationRefIds: string[];
  companyRefIds: string[];
  includeApplied: boolean;
  workplaceModes: WorkplaceMode[];
  workingTimeModes: WorkingTimeMode[];
}

/** The saved point the applied criteria measure from, or none; while the points are unknown, the applied one. */
const pointRefIdOf = (
  criteria: JobListingSearchCriteria,
  points: SavedPoint[],
  pointsLoaded: boolean
): string => {
  if (!criteria.point) return NONE;
  if (!pointsLoaded) return UNRESOLVED;
  return (
    points.find(
      p => p.lat === criteria.point?.lat && p.lon === criteria.point?.lon
    )?.refId ?? NONE
  );
};

const toDraft = (
  criteria: JobListingSearchCriteria,
  points: SavedPoint[],
  pointsLoaded: boolean
): Draft => ({
  pointRefId: pointRefIdOf(criteria, points, pointsLoaded),
  radiusKm: criteria.radiusKm === undefined ? '10' : String(criteria.radiusKm),
  hourlyRateMin:
    criteria.hourlyRateMin === undefined ? '' : String(criteria.hourlyRateMin),
  remoteOnly: !!criteria.remoteOnly,
  startDateFrom: criteria.startDateFrom ?? '',
  workingDays: criteria.workingDays ?? [],
  weeklyHoursMinMinutes:
    criteria.weeklyHoursMinMinutes === undefined
      ? NONE
      : String(criteria.weeklyHoursMinMinutes),
  weeklyHoursMaxMinutes:
    criteria.weeklyHoursMaxMinutes === undefined
      ? NONE
      : String(criteria.weeklyHoursMaxMinutes),
  query: criteria.query ?? '',
  occupationRefIds: criteria.occupationRefIds ?? [],
  companyRefIds: criteria.companyRefIds ?? [],
  includeApplied: criteria.applicationStatus === undefined,
  workplaceModes: criteria.workplaceModes ?? [],
  workingTimeModes: criteria.workingTimeModes ?? []
});

/**
 * The refIds still on offer. An occupation or company drops out of the options once its last visible listing goes,
 * and a filter by it would only ever answer an empty feed, so it is dropped too; but only once the options are
 * known, since before that every refId would look gone.
 */
const stillOffered = (
  refIds: string[],
  options: FilterOption[],
  optionsLoaded: boolean
): string[] =>
  optionsLoaded
    ? refIds.filter(refId => options.some(option => option.refId === refId))
    : refIds;

/** Only what was set travels; the sort follows the point. */
const toCriteria = (
  draft: Draft,
  points: SavedPoint[],
  options: FilterOptions,
  optionsLoaded: boolean,
  applied: JobListingSearchCriteria
): JobListingSearchCriteria => {
  const point =
    draft.pointRefId === UNRESOLVED
      ? applied.point
      : points.find(p => p.refId === draft.pointRefId);
  const criteria: JobListingSearchCriteria = {
    applicationStatus: draft.includeApplied
      ? undefined
      : ApplicationStatusFilter.NONE,
    sort: point ? JobListingSort.DISTANCE : JobListingSort.PUBLISHED_AT
  };
  if (point) {
    criteria.point = { lat: point.lat, lon: point.lon };
    criteria.radiusKm = Number(draft.radiusKm);
  }
  if (draft.hourlyRateMin !== '')
    criteria.hourlyRateMin = Number(draft.hourlyRateMin);
  if (draft.remoteOnly) criteria.remoteOnly = true;
  if (draft.startDateFrom) criteria.startDateFrom = draft.startDateFrom;
  if (draft.workingDays.length)
    criteria.workingDays = [...draft.workingDays].sort((a, b) => a - b);
  if (draft.weeklyHoursMinMinutes !== NONE)
    criteria.weeklyHoursMinMinutes = Number(draft.weeklyHoursMinMinutes);
  if (draft.weeklyHoursMaxMinutes !== NONE)
    criteria.weeklyHoursMaxMinutes = Number(draft.weeklyHoursMaxMinutes);
  if (draft.query.trim()) criteria.query = draft.query.trim();
  if (draft.workplaceModes.length)
    criteria.workplaceModes = [...draft.workplaceModes];
  if (draft.workingTimeModes.length)
    criteria.workingTimeModes = [...draft.workingTimeModes];
  const occupationRefIds = stillOffered(
    draft.occupationRefIds,
    options.occupations,
    optionsLoaded
  );
  if (occupationRefIds.length) criteria.occupationRefIds = occupationRefIds;
  const companyRefIds = stillOffered(
    draft.companyRefIds,
    options.companies,
    optionsLoaded
  );
  if (companyRefIds.length) criteria.companyRefIds = companyRefIds;
  return criteria;
};

/** Several of a list, by name; the refIds are what travels. */
const OptionsSelect = ({
  id,
  label,
  options,
  value,
  onChange
}: {
  id: string;
  label: string;
  options: FilterOption[];
  value: string[];
  onChange: (refIds: string[]) => void;
}) => (
  <Autocomplete
    multiple
    id={id}
    size="small"
    options={options}
    getOptionLabel={option => option.name}
    isOptionEqualToValue={(option, chosen) => option.refId === chosen.refId}
    value={options.filter(option => value.includes(option.refId))}
    onChange={(_, chosen) => onChange(chosen.map(option => option.refId))}
    filterSelectedOptions
    renderInput={params => <TextField {...params} label={label} />}
  />
);

type PlaceState = 'idle' | 'locating' | 'saved' | 'failed' | LocateFailure;
type SearchState = 'idle' | 'saving' | 'saved' | 'failed';

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Text
    variant="subtitle2"
    component="h3"
    color="text.primary"
    sx={{ fontWeight: 700 }}
  >
    {children}
  </Text>
);

const Caption = ({
  children,
  testId
}: {
  children: React.ReactNode;
  testId: string;
}) => (
  <Text
    variant="caption"
    color="text.secondary"
    component="p"
    data-testid={testId}
  >
    {children}
  </Text>
);

const NativeSelect = ({
  id,
  label,
  value,
  onChange,
  testId,
  disabled,
  children
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  testId: string;
  disabled?: boolean;
  children: React.ReactNode;
}) => (
  <TextField
    id={id}
    select
    disabled={disabled}
    fullWidth
    size="small"
    label={label}
    value={value}
    onChange={event => onChange(event.target.value)}
    SelectProps={{ native: true }}
    inputProps={{ 'data-testid': testId }}
  >
    {children}
  </TextField>
);

export const JobFeedFilters = ({
  open,
  onClose,
  criteria,
  savedPoints,
  preferencesLoaded,
  filterOptions,
  filterOptionsLoaded,
  onApply,
  onSavePoint,
  onSaveSearch,
  pjl = false
}: Props) => {
  const navigate = useNavigate();
  const [draft, setDraft] = useState<Draft>(() =>
    toDraft(criteria, savedPoints, preferencesLoaded)
  );
  // The points as the last save answered them, until the refetched preferences arrive with the same list, so a place
  // just saved can be chosen at once.
  const [pointsFromSave, setPointsFromSave] = useState<SavedPoint[] | null>(
    null
  );
  const points = pointsFromSave ?? savedPoints;
  const [placeLabel, setPlaceLabel] = useState('');
  const [placeState, setPlaceState] = useState<PlaceState>('idle');
  const [searchLabel, setSearchLabel] = useState('');
  const [searchState, setSearchState] = useState<SearchState>('idle');
  const wasOpen = useRef(false);

  // Reopening shows what is applied, not what was typed and abandoned last time. Only on opening: the saved points
  // change while the sheet is open when a place is saved, and that must not throw away the rest of the draft.
  useEffect(() => {
    if (open && !wasOpen.current) {
      setDraft(toDraft(criteria, savedPoints, preferencesLoaded));
      setPlaceState('idle');
      setSearchState('idle');
    }
    wasOpen.current = open;
  }, [open, criteria, savedPoints, preferencesLoaded]);

  // Opened before the saved points arrived: the applied point waited as it was, and now finds its saved point. Only
  // the point; whatever else the talent changed meanwhile stays. The select was disabled, so nobody chose otherwise.
  useEffect(() => {
    if (!preferencesLoaded) return;
    setDraft(current =>
      current.pointRefId === UNRESOLVED
        ? { ...current, pointRefId: pointRefIdOf(criteria, savedPoints, true) }
        : current
    );
  }, [preferencesLoaded, criteria, savedPoints]);

  useEffect(() => setPointsFromSave(null), [savedPoints]);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft(current => ({ ...current, [key]: value }));

  const current = () =>
    toCriteria(draft, points, filterOptions, filterOptionsLoaded, criteria);

  const apply = () => {
    onApply(current());
    onClose();
  };

  const canAddPoint = points.length < MAX_SAVED_POINTS;

  /** Where I stand, under the label I gave it, and chosen as the place to measure from. */
  const savePlace = async () => {
    const label = placeLabel.trim();
    setPlaceState('locating');
    try {
      const point = await locateMe();
      const stored = await onSavePoint({ label, ...point });
      setPointsFromSave(stored.savedPoints);
      const saved = stored.savedPoints.find(
        p => p.label === label && p.lat === point.lat && p.lon === point.lon
      );
      if (saved?.refId) set('pointRefId', saved.refId);
      setPlaceLabel('');
      setPlaceState('saved');
    } catch (error) {
      setPlaceState(error instanceof LocateError ? error.reason : 'failed');
    }
  };

  /** The sheet as it stands, not as last applied: what the talent sees is what they keep. */
  const saveSearch = async () => {
    setSearchState('saving');
    try {
      await onSaveSearch({ label: searchLabel.trim(), criteria: current() });
      setSearchLabel('');
      setSearchState('saved');
    } catch {
      setSearchState('failed');
    }
  };

  const clear = () => {
    onApply({ ...DEFAULT_CRITERIA, sort: JobListingSort.PUBLISHED_AT });
    onClose();
  };

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      ariaLabel={getTrans('jobs.filters.title')}
    >
      <Box sx={{ p: 3, display: 'grid', gap: 2.5, overflowY: 'auto' }}>
        <Text
          variant="h5"
          component="h2"
          color="text.primary"
          sx={{ fontWeight: 700 }}
        >
          {getTrans('jobs.filters.title')}
        </Text>

        <NativeSelect
          id="jobs-filter-point"
          testId="jobs-filter-point"
          label={getTrans('jobs.filters.point')}
          value={draft.pointRefId}
          onChange={value => set('pointRefId', value)}
          disabled={!preferencesLoaded}
        >
          {draft.pointRefId === UNRESOLVED && (
            <option value={UNRESOLVED}>…</option>
          )}
          <option value={NONE}>{getTrans('jobs.filters.pointNone')}</option>
          {points.map(point => (
            <option key={point.refId ?? point.label} value={point.refId}>
              {point.label}
            </option>
          ))}
        </NativeSelect>

        {draft.pointRefId !== NONE && (
          <NativeSelect
            id="jobs-filter-radius"
            testId="jobs-filter-radius"
            label={getTrans('jobs.filters.radius')}
            value={draft.radiusKm}
            onChange={value => set('radiusKm', value)}
          >
            {RADII_KM.map(km => (
              <option key={km} value={km}>
                {getTransWithParams('jobs.filters.radiusKm', {
                  km: String(km)
                })}
              </option>
            ))}
          </NativeSelect>
        )}

        <TextField
          id="jobs-filter-hourly-rate-min"
          type="number"
          size="small"
          fullWidth
          label={getTrans('jobs.filters.hourlyRateMin')}
          value={draft.hourlyRateMin}
          onChange={event => set('hourlyRateMin', event.target.value)}
          inputProps={{ min: 0, step: 0.5 }}
        />

        {pjl ? (
          <>
            {/* No "passt zu meiner Woche" switch yet: pro-core accepts fitsMyWeek but narrows nothing by it. */}
            <ModeChoice
              title={getTrans('jobs.filters.workplace')}
              testId="jobs-filter-workplace"
              options={WORKPLACE_MODES}
              labelPrefix="jobs.filters.workplace"
              value={draft.workplaceModes}
              onChange={modes => set('workplaceModes', modes)}
            />
            <ModeChoice
              title={getTrans('jobs.filters.workingTime')}
              testId="jobs-filter-working-time"
              options={WORKING_TIME_MODES}
              labelPrefix="jobs.filters.workingTime"
              value={draft.workingTimeModes}
              onChange={modes => set('workingTimeModes', modes)}
            />
          </>
        ) : (
          <FormControlLabel
            control={
              <Switch
                checked={draft.remoteOnly}
                onChange={(_, checked) => set('remoteOnly', checked)}
              />
            }
            label={getTrans('jobs.filters.remoteOnly')}
          />
        )}

        <TextField
          id="jobs-filter-start-from"
          type="date"
          size="small"
          fullWidth
          label={getTrans('jobs.filters.startDateFrom')}
          value={draft.startDateFrom}
          onChange={event => set('startDateFrom', event.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <Box>
          <Text
            variant="caption"
            color="text.secondary"
            component="p"
            sx={{ mb: 1 }}
          >
            {getTrans('jobs.filters.workingDays')}
          </Text>
          <ToggleButtonGroup
            size="small"
            value={draft.workingDays}
            onChange={(_, days: number[]) => set('workingDays', days)}
            aria-label={getTrans('jobs.filters.workingDays')}
            sx={{ flexWrap: 'wrap' }}
          >
            {DAYS.map(day => (
              <ToggleButton
                key={day}
                value={day}
                aria-label={getTrans(
                  `jobs.filters.day.${day}` as TranslationKey
                )}
              >
                {getTrans(`jobs.filters.day.${day}` as TranslationKey)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
          <NativeSelect
            id="jobs-filter-hours-min"
            testId="jobs-filter-hours-min"
            label={getTrans('jobs.filters.weeklyHoursMin')}
            value={draft.weeklyHoursMinMinutes}
            onChange={value => set('weeklyHoursMinMinutes', value)}
          >
            <option value={NONE}>{getTrans('jobs.filters.any')}</option>
            {HOURS_MINUTES.map(minutes => (
              <option key={minutes} value={minutes}>
                {getTransWithParams('jobs.filters.hoursOption', {
                  hours: String(minutes / 60)
                })}
              </option>
            ))}
          </NativeSelect>
          <NativeSelect
            id="jobs-filter-hours-max"
            testId="jobs-filter-hours-max"
            label={getTrans('jobs.filters.weeklyHoursMax')}
            value={draft.weeklyHoursMaxMinutes}
            onChange={value => set('weeklyHoursMaxMinutes', value)}
          >
            <option value={NONE}>{getTrans('jobs.filters.any')}</option>
            {HOURS_MINUTES.map(minutes => (
              <option key={minutes} value={minutes}>
                {getTransWithParams('jobs.filters.hoursOption', {
                  hours: String(minutes / 60)
                })}
              </option>
            ))}
          </NativeSelect>
        </Box>

        <TextField
          id="jobs-filter-query"
          size="small"
          fullWidth
          label={getTrans('jobs.filters.query')}
          value={draft.query}
          onChange={event => set('query', event.target.value)}
          inputProps={{ maxLength: 200 }}
        />

        {filterOptions.occupations.length > 0 && (
          <OptionsSelect
            id="jobs-filter-occupations"
            label={getTrans('jobs.filters.occupations')}
            options={filterOptions.occupations}
            value={draft.occupationRefIds}
            onChange={refIds => set('occupationRefIds', refIds)}
          />
        )}

        {filterOptions.companies.length > 0 && (
          <OptionsSelect
            id="jobs-filter-companies"
            label={getTrans('jobs.filters.companies')}
            options={filterOptions.companies}
            value={draft.companyRefIds}
            onChange={refIds => set('companyRefIds', refIds)}
          />
        )}

        <FormControlLabel
          control={
            <Switch
              checked={draft.includeApplied}
              onChange={(_, checked) => set('includeApplied', checked)}
            />
          }
          label={getTrans('jobs.filters.includeApplied')}
        />

        <Box
          component="section"
          aria-label={getTrans('jobs.filters.savePlace.title')}
          sx={{ display: 'grid', gap: 1.5 }}
        >
          <SectionTitle>
            {getTrans('jobs.filters.savePlace.title')}
          </SectionTitle>
          {canAddPoint ? (
            <>
              <TextField
                id="jobs-save-place-label"
                size="small"
                fullWidth
                label={getTrans('jobs.filters.savePlace.label')}
                value={placeLabel}
                onChange={event => setPlaceLabel(event.target.value)}
                inputProps={{ maxLength: MAX_LABEL_LENGTH }}
              />
              <AppButton
                variant="outlined"
                onClick={() => void savePlace()}
                disabled={
                  !preferencesLoaded ||
                  !placeLabel.trim() ||
                  placeState === 'locating'
                }
                testId="jobs-save-place"
              >
                {getTrans('jobs.filters.savePlace.button')}
              </AppButton>
            </>
          ) : (
            <Caption testId="jobs-save-place-limit">
              {getTrans('jobs.filters.savePlace.limit')}
            </Caption>
          )}
          {placeState === 'saved' && (
            <Caption testId="jobs-save-place-saved">
              {getTrans('jobs.filters.savePlace.saved')}
            </Caption>
          )}
          {placeState === 'unavailable' && (
            <Caption testId="jobs-save-place-unavailable">
              {getTrans('jobs.filters.savePlace.unavailable')}
            </Caption>
          )}
          {placeState === 'failed' && (
            <Caption testId="jobs-save-place-failed">
              {getTrans('jobs.filters.saveFailed')}
            </Caption>
          )}
          {placeState === 'denied' && (
            <Box sx={{ display: 'grid', gap: 1 }}>
              <Caption testId="jobs-save-place-denied">
                {getTrans('jobs.filters.savePlace.denied')}
              </Caption>
              {/* The guide the shift check-in sends talents to: how to turn location access back on, per device. */}
              <AppButton
                variant="text"
                onClick={() =>
                  navigate(
                    `${PATHS.account}/${PATHS.privacySettings}/${PATHS.geolocation}`
                  )
                }
                testId="jobs-save-place-settings"
              >
                {getTrans('jobs.filters.savePlace.settings')}
              </AppButton>
            </Box>
          )}
        </Box>

        <Box
          component="section"
          aria-label={getTrans('jobs.filters.saveSearch.title')}
          sx={{ display: 'grid', gap: 1.5 }}
        >
          <SectionTitle>
            {getTrans('jobs.filters.saveSearch.title')}
          </SectionTitle>
          <TextField
            id="jobs-save-search-label"
            size="small"
            fullWidth
            label={getTrans('jobs.filters.saveSearch.label')}
            value={searchLabel}
            onChange={event => setSearchLabel(event.target.value)}
            inputProps={{ maxLength: MAX_LABEL_LENGTH }}
          />
          <AppButton
            variant="outlined"
            onClick={() => void saveSearch()}
            disabled={
              !preferencesLoaded ||
              !searchLabel.trim() ||
              searchState === 'saving'
            }
            testId="jobs-save-search"
          >
            {getTrans('jobs.filters.saveSearch.button')}
          </AppButton>
          {searchState === 'saved' && (
            <Caption testId="jobs-save-search-saved">
              {getTrans('jobs.filters.saveSearch.saved')}
            </Caption>
          )}
          {searchState === 'failed' && (
            <Caption testId="jobs-save-search-failed">
              {getTrans('jobs.filters.saveFailed')}
            </Caption>
          )}
        </Box>

        <Box
          sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end', pt: 1 }}
        >
          <AppButton variant="text" onClick={clear} testId="jobs-filter-clear">
            {getTrans('jobs.filters.clear')}
          </AppButton>
          <AppButton onClick={apply} testId="jobs-filter-apply">
            {getTrans('jobs.filters.apply')}
          </AppButton>
        </Box>
      </Box>
    </BottomSheet>
  );
};
